// WbsWidget — renders the `wbs-tree` envelope across four modes:
// Hierarchy (boxed tree, default), Indented tree (text outline),
// Mindmap (Markmap iframe), and Sunburst (placeholder until a real
// d3-hierarchy implementation lands).
//
// Right-pin-pane FOCUSED BRANCH content is pushed via useRightPane when
// the user selects a phase. WBS coding outline is rendered inline in the
// pin pane as well.

import { useEffect, useMemo, useState } from "react";
import { useRightPane } from "../ShellContext.js";

interface WbsTask {
  id: string;
  name: string;
  duration_days: number | null;
  is_critical: boolean;
  resources: string[];
  is_milestone?: boolean;
}

interface WbsPhase {
  id: string;
  code: string | null;
  name: string;
  summary: string | null;
  start: string | null;
  end: string | null;
  progress: number | null;
  task_count: number;
  milestone_count: number;
  critical_count: number;
  has_critical: boolean;
  tasks: WbsTask[];
  milestones: WbsTask[];
}

interface WbsPayload {
  project_id: string;
  project_name: string;
  phases: WbsPhase[];
  summary: {
    phase_count: number;
    task_count: number;
    milestone_count: number;
    critical_count: number;
  };
  iframe_url: string | null;
}

type Mode = "hierarchy" | "indented" | "mindmap" | "sunburst";

const MODES: Array<{ id: Mode; label: string }> = [
  { id: "hierarchy", label: "Hierarchy" },
  { id: "indented",  label: "Indented tree" },
  { id: "mindmap",   label: "Mindmap" },
  { id: "sunburst",  label: "Sunburst" },
];

export function WbsWidget({ data }: { data: unknown }) {
  const payload = (data ?? {}) as Partial<WbsPayload>;
  const phases = payload.phases ?? [];
  const summary = payload.summary ?? { phase_count: 0, task_count: 0, milestone_count: 0, critical_count: 0 };
  const projectName = payload.project_name ?? payload.project_id ?? "(no project)";
  const [mode, setMode] = useState<Mode>("hierarchy");
  // Default-focus the first phase that contains a critical task so the
  // hierarchy view has a sensible initial expansion.
  const defaultFocusId = useMemo(() => {
    const withCritical = phases.find((p) => p.has_critical);
    return (withCritical ?? phases[0])?.id ?? null;
  }, [phases]);
  const [focusId, setFocusId] = useState<string | null>(defaultFocusId);
  useEffect(() => {
    if (focusId == null) setFocusId(defaultFocusId);
  }, [defaultFocusId, focusId]);

  const focused = focusId ? phases.find((p) => p.id === focusId) ?? null : null;

  useRightPane(
    useMemo(() => (
      <FocusedBranchPane phases={phases} focused={focused} />
    ), [phases, focused]),
  );

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <Toolbar
        mode={mode}
        setMode={setMode}
        summary={summary}
        iframeUrl={payload.iframe_url ?? null}
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {mode === "hierarchy" && (
          <HierarchyView
            project={projectName}
            phases={phases}
            summary={summary}
            focusId={focusId}
            onFocus={setFocusId}
          />
        )}
        {mode === "indented" && (
          <IndentedView phases={phases} focusId={focusId} onFocus={setFocusId} />
        )}
        {mode === "mindmap" && (
          <MindmapView iframeUrl={payload.iframe_url ?? null} />
        )}
        {mode === "sunburst" && (
          <div className="py-12 text-center text-sm text-slate-500">
            Sunburst mode is on the roadmap. For now, use Hierarchy / Indented / Mindmap.
          </div>
        )}
      </div>
    </div>
  );
}

function Toolbar({ mode, setMode, summary, iframeUrl }: {
  mode: Mode;
  setMode: (m: Mode) => void;
  summary: WbsPayload["summary"];
  iframeUrl: string | null;
}) {
  return (
    <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <div className="flex items-center gap-3 text-[11px] text-slate-600">
        <LegendChip label="project" className="bg-slate-900" />
        <LegendChip label="phase" className="bg-sky-200 border border-sky-300" />
        <LegendChip label="phase contains critical task" className="bg-rose-100 border border-rose-300" />
        <LegendChip label="task" className="bg-white border border-slate-300" />
        <LegendChip label="milestone ◆" className="bg-amber-300" />
        <span className="text-slate-500">
          {summary.phase_count} phases · {summary.task_count} tasks · {summary.milestone_count} milestones
        </span>
      </div>
      <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={
              "rounded px-2 py-0.5 " +
              (mode === m.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")
            }
          >{m.label}</button>
        ))}
        {iframeUrl && (
          <a href={iframeUrl} target="_blank" rel="noreferrer"
             className="ml-1 rounded px-2 py-0.5 text-slate-500 hover:bg-slate-100 hover:text-sky-700"
             title="Open Markmap mindmap in a new tab">↗</a>
        )}
      </div>
    </div>
  );
}

// ---- Hierarchy mode ------------------------------------------------------

function HierarchyView({ project, phases, summary, focusId, onFocus }: {
  project: string;
  phases: WbsPhase[];
  summary: WbsPayload["summary"];
  focusId: string | null;
  onFocus: (id: string) => void;
}) {
  const focused = focusId ? phases.find((p) => p.id === focusId) ?? null : null;
  const otherPhases = phases.filter((p) => p.id !== focusId);
  return (
    <div className="flex flex-col items-center gap-6">
      {/* L0 — root */}
      <div className="rounded-lg bg-slate-900 px-4 py-3 text-center text-sm text-white shadow-sm">
        <div className="font-semibold">{project}</div>
        <div className="text-[11px] text-slate-300">
          {summary.task_count} tasks · {summary.phase_count} phases
        </div>
      </div>

      {/* L1 — phases. Critical-containing phases get rose border. */}
      <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {phases.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onFocus(p.id)}
            className={
              "rounded-md border bg-white px-3 py-2 text-left text-xs shadow-sm transition-colors " +
              (p.id === focusId ? "ring-2 ring-slate-900 " : "") +
              (p.has_critical
                ? "border-rose-300 bg-rose-50 hover:bg-rose-100"
                : "border-sky-200 hover:bg-sky-50")
            }
          >
            <div className="font-medium text-slate-900">
              {p.code ? `${p.code} ` : ""}{p.name}
              {p.has_critical && <span className="ml-1 text-rose-600">✦</span>}
            </div>
            <div className="text-[10px] text-slate-500">
              {p.task_count} task{p.task_count === 1 ? "" : "s"}
              {p.progress != null ? ` · ${Math.round(p.progress * 100)}%` : ""}
              {p.critical_count > 0 ? ` · ${p.critical_count} critical` : ""}
            </div>
            {p.progress != null && (
              <div className="mt-1 h-1 rounded bg-slate-100">
                <div className={p.has_critical ? "h-full rounded bg-rose-400" : "h-full rounded bg-sky-400"}
                     style={{ width: `${Math.round((p.progress ?? 0) * 100)}%` }} />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* L2 — leaf tasks under the focused phase. */}
      {focused && (
        <div className="w-full">
          <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">
            L2 tasks · {focused.code ? `${focused.code} ` : ""}{focused.name}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {focused.tasks.map((t) => (
              <div
                key={t.id}
                className={
                  "rounded-md border px-2 py-1.5 text-[11px] " +
                  (t.is_critical
                    ? "border-rose-300 bg-rose-50 text-rose-700"
                    : "border-slate-200 bg-white text-slate-700")
                }
                title={t.resources.length ? `Resources: ${t.resources.join(", ")}` : undefined}
              >
                <div className="truncate font-mono text-[10px] text-slate-500">
                  {t.id}{t.is_critical ? " ✦" : ""}
                </div>
                <div className="truncate">{shortName(t.name)}</div>
                {t.duration_days != null && (
                  <div className="text-[10px] text-slate-400">{t.duration_days}d</div>
                )}
              </div>
            ))}
            {focused.milestones.map((m) => (
              <div key={m.id}
                   className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                <div className="font-mono text-[10px]">{m.id}</div>
                <div className="truncate">◆ {shortName(m.name)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collapsed peers — click to focus. */}
      {otherPhases.length > 0 && (
        <div className="w-full">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">
            Other phases (collapsed)
          </div>
          <div className="text-[11px] text-slate-400">
            click a phase box above to expand its task children below
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Indented tree mode --------------------------------------------------

function IndentedView({ phases, focusId, onFocus }: {
  phases: WbsPhase[];
  focusId: string | null;
  onFocus: (id: string) => void;
}) {
  return (
    <div className="space-y-1 font-mono text-xs">
      {phases.map((p) => (
        <div key={p.id}>
          <button
            type="button"
            onClick={() => onFocus(p.id)}
            className={
              "block w-full rounded px-1 py-0.5 text-left " +
              (p.id === focusId
                ? "bg-slate-900 text-white"
                : "text-slate-800 hover:bg-slate-100") +
              (p.has_critical && p.id !== focusId ? " text-rose-700" : "")
            }
          >
            {p.code ? `${p.code} ` : ""}{p.name}
            <span className="ml-2 text-[10px] opacity-70">
              ({p.task_count} tasks{p.critical_count > 0 ? `, ${p.critical_count} critical` : ""})
            </span>
          </button>
          {p.id === focusId && (
            <div className="ml-4 space-y-0.5 border-l border-slate-200 pl-3">
              {p.tasks.map((t) => (
                <div key={t.id}
                     className={t.is_critical ? "text-rose-700" : "text-slate-700"}>
                  {t.id}{t.is_critical ? " ✦" : ""} · {shortName(t.name)}
                  {t.duration_days != null ? <span className="ml-1 text-[10px] text-slate-400">({t.duration_days}d)</span> : null}
                </div>
              ))}
              {p.milestones.map((m) => (
                <div key={m.id} className="text-amber-700">
                  {m.id} · ◆ {shortName(m.name)}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- Mindmap mode (Markmap iframe) ---------------------------------------

function MindmapView({ iframeUrl }: { iframeUrl: string | null }) {
  if (!iframeUrl) {
    return (
      <div className="py-12 text-center text-sm text-slate-500">
        Markmap output not yet generated. Run Rebuild to produce wbs.html.
      </div>
    );
  }
  // key={iframeUrl} + cache-bust same trick as Critical-path and Resources
  // Bottleneck iframes — prevents the initial-paint race that left the
  // Mindmap pane visually blank (Bug #2, 2026-05-20).
  const src = iframeUrl + (iframeUrl.includes("?") ? "&" : "?") + "_=" + Date.now();
  return (
    <iframe
      key={iframeUrl}
      src={src}
      title="Work-breakdown mindmap"
      className="h-full w-full border-0"
    />
  );
}

// ---- Right pin pane: FOCUSED BRANCH + WBS CODING --------------------------

function FocusedBranchPane({ phases, focused }: {
  phases: WbsPhase[];
  focused: WbsPhase | null;
}) {
  if (!focused) {
    return (
      <div className="rounded border border-dashed border-slate-200 p-3 text-xs text-slate-500">
        Click a phase to focus it. The right pane shows the branch's property
        grid, its leaf tasks (critical ones in rose), and the WBS coding outline.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Focused branch</div>
        <div className="mt-2 rounded border border-slate-200 p-2 text-xs">
          <div className="font-medium">
            {focused.code ? `${focused.code} ` : ""}{focused.name} ·{" "}
            <span className="text-slate-500">{focused.task_count} tasks</span>
          </div>
          <dl className="mt-2 grid grid-cols-[80px_minmax(0,1fr)] gap-y-1 text-[11px]">
            <dt className="text-slate-500">WBS code</dt><dd>{focused.code ?? "—"}</dd>
            <dt className="text-slate-500">progress</dt>
            <dd>{focused.progress == null ? "—" : `${Math.round(focused.progress * 100)}%`}</dd>
            <dt className="text-slate-500">start</dt><dd>{focused.start ?? "—"}</dd>
            <dt className="text-slate-500">end</dt><dd>{focused.end ?? "—"}</dd>
            <dt className="text-slate-500">critical</dt>
            <dd className={focused.critical_count > 0 ? "text-rose-600" : ""}>
              {focused.critical_count} of {focused.task_count}
            </dd>
          </dl>
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {focused.tasks.map((t) => (
              <li key={t.id} className={t.is_critical ? "text-rose-700" : "text-slate-700"}>
                • <span className="font-mono text-[10px]">{t.id}</span>{" "}
                {shortName(t.name)}{t.is_critical ? " ✦" : ""}
              </li>
            ))}
            {focused.milestones.map((m) => (
              <li key={m.id} className="text-amber-700">
                • <span className="font-mono text-[10px]">{m.id}</span>{" "}
                ◆ {shortName(m.name)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">WBS coding</div>
        <pre className="mt-2 max-h-56 overflow-auto whitespace-pre rounded border border-slate-200 bg-slate-50 p-2 text-[11px] leading-5 text-slate-700">
{outline(phases, focused.id)}
        </pre>
      </section>
    </div>
  );
}

function outline(phases: WbsPhase[], focusId: string): string {
  const lines: string[] = ["1 project"];
  phases.forEach((p, i) => {
    const code = p.code ?? `1.${i + 1}`;
    const marker = p.id === focusId ? "  ← focused" : "";
    lines.push(`${code} ${p.name}${marker}`);
    if (p.id === focusId) {
      p.tasks.slice(0, 6).forEach((t, j) => {
        lines.push(`${code}.${j + 1} ${t.id}${t.is_critical ? " ✦" : ""}`);
      });
      if (p.tasks.length > 6) lines.push(`  … ${p.tasks.length - 6} more`);
    }
  });
  return lines.join("\n");
}

function shortName(name: string): string {
  return name.length > 60 ? name.slice(0, 57) + "…" : name;
}

function LegendChip({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`h-2.5 w-2.5 rounded-sm ${className}`} />
      <span>{label}</span>
    </span>
  );
}
